import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Endpoints that must NEVER trigger a refresh-and-retry (avoids infinite loops / nonsensical retries).
const NO_REFRESH_RETRY_PATHS = ["/auth/login", "/auth/register", "/auth/register-recruiter", "/auth/refresh", "/auth/logout"];

let isRefreshing = false;
let refreshWaiters = [];

// A transient 401 (e.g. the 15-minute access-token cookie expiring mid-session, or a brief
// cookie-propagation race right after another mutating request) previously flipped the whole
// app's auth state to logged-out immediately — even though the httpOnly refresh_token cookie
// (7-day validity) was still perfectly valid. This silently unmounted any user-gated UI (e.g.
// TrackPreferenceModal), losing in-progress local state like its multi-step `step`. Now: on any
// authenticated request's 401, try POST /auth/refresh once, and only retry-once the original
// request if that succeeds; only truly log out if the refresh token itself is also invalid/expired.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const isAuthBootstrapPath = config?.url && NO_REFRESH_RETRY_PATHS.some((p) => config.url.includes(p));
    if (response?.status !== 401 || config?._retriedAfterRefresh || isAuthBootstrapPath) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshWaiters.push({ resolve, reject });
      }).then(() => {
        config._retriedAfterRefresh = true;
        return api(config);
      });
    }

    config._retriedAfterRefresh = true;
    isRefreshing = true;
    try {
      await api.post("/auth/refresh");
      isRefreshing = false;
      refreshWaiters.forEach((w) => w.resolve());
      refreshWaiters = [];
      return api(config);
    } catch (refreshError) {
      isRefreshing = false;
      refreshWaiters.forEach((w) => w.reject(refreshError));
      refreshWaiters = [];
      return Promise.reject(error);
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
